import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Divider, Grid, MenuItem, TextField, Typography, useTheme} from '@mui/material';

import {DepartmentTargetProfileRow} from './DepartmentTargetProfileRow';
import {DepartmentTargetProfileViewDialog} from './DepartmentTargetProfileViewDialog';
import {TargetProfileDetailCard} from './TargetProfileDetailCard';
import {BasicTable} from '../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {ConfirmDeleteDialog} from '../../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {Loading} from '../../../../../components/Loading/Loading';
import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import {Delete} from '../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../lib/api/DELETE/DeleteRoutes';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {DepartmentTargetProfileRequestBodies} from '../../../../../lib/api/RequestBodies/features/company/workforce/DepartmentTargetProfileRequestBodies';
import {ModuleTypes} from '../../../../../types/CompanyProductConfiguration/CompanyModule';
import {ProfessionalArea} from '../../../../../types/ProfessionalArea';
import {isCompanyModuleActive} from '../../../../../utils/companyModuleUtils/isCompanyModuleActive';
import {
  mapDepartmentTargetProfilesResponse,
  mapProfessionalAreasConfigResponse,
  mapTargetProfileDetailResponse,
  mapTargetProfilesCatalogResponse,
} from '../../serializers/DepartmentTargetProfileSerializers';
import {
  DepartmentTargetProfileAssignment,
  TargetProfileCatalogItem,
  TargetProfileDetail,
} from '../../types/DepartmentTargetProfileTypes';

const EMPTY_PROFESSIONAL_AREA = '';

export interface Props {
  companyId: string;
  departmentId: string;
}

export const DepartmentTargetProfilesSection = ({companyId, departmentId}: Props) => {
  const theme = useTheme();
  const currentLanguage = useContext(LanguageContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {productConfiguration} = useContext(CompanyContext);

  const isWorkforceModuleActive = isCompanyModuleActive(
    ModuleTypes.WorkforceModule,
    productConfiguration.data?.companyModules
  );
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal.targetProfiles'});
  const {t: tShared} = useTranslation();

  const [professionalAreaFilter, setProfessionalAreaFilter] = useState(EMPTY_PROFESSIONAL_AREA);
  const [selectedCatalogProfileId, setSelectedCatalogProfileId] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [viewingAssignmentId, setViewingAssignmentId] = useState<string | null>(null);
  const [excludedCoreSkillIds, setExcludedCoreSkillIds] = useState<number[]>([]);
  const [excludedHardSkillIds, setExcludedHardSkillIds] = useState<number[]>([]);
  const [excludedToolSkillIds, setExcludedToolSkillIds] = useState<number[]>([]);
  const [excludedDomainKnowledgeSkillIds, setExcludedDomainKnowledgeSkillIds] = useState<number[]>([]);
  const [excludedSoftSkillIds, setExcludedSoftSkillIds] = useState<number[]>([]);
  const [excludedResponsibilityIds, setExcludedResponsibilityIds] = useState<number[]>([]);

  const isProfessionalAreaSelected = professionalAreaFilter !== EMPTY_PROFESSIONAL_AREA;

  const {data: professionalAreas, isLoading: isProfessionalAreasLoading} = useApiRead<ProfessionalArea[]>({
    path: GetRoutes.professionalAreas,
    camelCaseOverride: mapProfessionalAreasConfigResponse,
    options: {queryKey: ['professionalAreas'], enabled: isWorkforceModuleActive},
  });

  const {
    data: assignments = [],
    isLoading: isAssignmentsLoading,
    invalidateCache: invalidateAssignments,
  } = useApiRead<DepartmentTargetProfileAssignment[]>({
    path: GetRoutes.companyDepartmentTargetProfiles(companyId, departmentId),
    camelCaseOverride: mapDepartmentTargetProfilesResponse,
    options: {
      queryKey: ['companyDepartmentTargetProfiles', companyId, departmentId],
      enabled: isWorkforceModuleActive && !!companyId && !!departmentId,
    },
  });

  const {
    data: selectedProfileDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useApiRead<TargetProfileDetail | undefined>({
    path: GetRoutes.targetProfile(selectedCatalogProfileId),
    camelCaseOverride: mapTargetProfileDetailResponse,
    options: {
      queryKey: ['targetProfile', selectedCatalogProfileId],
      enabled: isWorkforceModuleActive && !!selectedCatalogProfileId,
    },
  });

  const {
    data: catalogProfiles = [],
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    invalidateCache: invalidateCatalog,
  } = useApiRead<TargetProfileCatalogItem[]>({
    path: GetRoutes.targetProfiles(Number(professionalAreaFilter)),
    camelCaseOverride: mapTargetProfilesCatalogResponse,
    options: {
      queryKey: ['targetProfiles', professionalAreaFilter],
      enabled: isWorkforceModuleActive && !!companyId && !!departmentId && isProfessionalAreaSelected,
    },
  });

  const assignedCatalogIds = useMemo(
    () => new Set(assignments.map((assignment) => assignment.targetProfile.resourceId)),
    [assignments]
  );

  const availableCatalogProfiles = useMemo(
    () => catalogProfiles.filter((profile) => !assignedCatalogIds.has(profile.resourceId)),
    [assignedCatalogIds, catalogProfiles]
  );

  const {makeRequest: assignTargetProfile, isLoading: isAssigning, error: assignError} = useApiWrite<
    Post.AssignDepartmentTargetProfile,
    DepartmentTargetProfileRequestBodies[Post.AssignDepartmentTargetProfile]
  >({
    method: 'post',
    path: PostRoutes[Post.AssignDepartmentTargetProfile](companyId, departmentId),
    onSuccess: () => {
      invalidateAssignments();
      invalidateCatalog();
      setSelectedCatalogProfileId('');
      setExcludedCoreSkillIds([]);
      setExcludedHardSkillIds([]);
      setExcludedToolSkillIds([]);
      setExcludedDomainKnowledgeSkillIds([]);
      setExcludedSoftSkillIds([]);
      setExcludedResponsibilityIds([]);
      dispatchMessage(tShared('shared.formSubmit.success'), 'success');
    },
  });

  const {makeManualRequestWithParams: deleteAssignment, error: deleteError} = useApiWrite<
    Delete.DeleteDepartmentTargetProfile
  >({
    onSuccess: () => {
      invalidateAssignments();
      invalidateCatalog();
      setPendingDeleteId(null);
      dispatchMessage(tShared('shared.formSubmit.success'), 'success');
    },
  });

  useEffect(() => {
    setSelectedCatalogProfileId('');
    setExcludedCoreSkillIds([]);
    setExcludedHardSkillIds([]);
    setExcludedToolSkillIds([]);
    setExcludedDomainKnowledgeSkillIds([]);
    setExcludedSoftSkillIds([]);
    setExcludedResponsibilityIds([]);
  }, [professionalAreaFilter]);

  useEffect(() => {
    setExcludedCoreSkillIds([]);
    setExcludedHardSkillIds([]);
    setExcludedToolSkillIds([]);
    setExcludedDomainKnowledgeSkillIds([]);
    setExcludedSoftSkillIds([]);
    setExcludedResponsibilityIds([]);
  }, [selectedCatalogProfileId]);

  const excludedCoreSkillIdSet = useMemo(() => new Set(excludedCoreSkillIds), [excludedCoreSkillIds]);
  const excludedHardSkillIdSet = useMemo(() => new Set(excludedHardSkillIds), [excludedHardSkillIds]);
  const excludedToolSkillIdSet = useMemo(() => new Set(excludedToolSkillIds), [excludedToolSkillIds]);
  const excludedDomainKnowledgeSkillIdSet = useMemo(
    () => new Set(excludedDomainKnowledgeSkillIds),
    [excludedDomainKnowledgeSkillIds]
  );
  const excludedSoftSkillIdSet = useMemo(() => new Set(excludedSoftSkillIds), [excludedSoftSkillIds]);
  const excludedResponsibilityIdSet = useMemo(
    () => new Set(excludedResponsibilityIds),
    [excludedResponsibilityIds]
  );

  const toggleCoreExclusion = useCallback((standardSkillId: number) => {
    setExcludedCoreSkillIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  const toggleHardExclusion = useCallback((standardSkillId: number) => {
    setExcludedHardSkillIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  const toggleToolExclusion = useCallback((standardSkillId: number) => {
    setExcludedToolSkillIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  const toggleDomainKnowledgeExclusion = useCallback((standardSkillId: number) => {
    setExcludedDomainKnowledgeSkillIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  const toggleSoftExclusion = useCallback((standardSkillId: number) => {
    setExcludedSoftSkillIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  const toggleResponsibilityExclusion = useCallback((standardSkillId: number) => {
    setExcludedResponsibilityIds((current) =>
      current.includes(standardSkillId)
        ? current.filter((id) => id !== standardSkillId)
        : [...current, standardSkillId]
    );
  }, []);

  useEffect(() => {
    if (assignError) {
      dispatchMessage(tShared('shared.formSubmit.creationError'), 'error');
    }
  }, [assignError, dispatchMessage, tShared]);

  useEffect(() => {
    if (deleteError) {
      dispatchMessage(tShared('shared.formSubmit.deletionError'), 'error');
      setPendingDeleteId(null);
    }
  }, [deleteError, dispatchMessage, tShared]);

  useEffect(() => {
    if (isCatalogError) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [dispatchMessage, isCatalogError, tShared]);

  useEffect(() => {
    if (isDetailError) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [dispatchMessage, isDetailError, tShared]);

  const pickerHelperText = useMemo(() => {
    if (!isProfessionalAreaSelected) {
      return undefined;
    }
    if (isCatalogError) {
      return tShared('shared.fetchingData.error');
    }
    if (!isCatalogLoading && !availableCatalogProfiles.length) {
      return t('picker.noProfilesAvailable');
    }
    return undefined;
  }, [
    availableCatalogProfiles.length,
    isCatalogError,
    isCatalogLoading,
    isProfessionalAreaSelected,
    t,
    tShared,
  ]);

  const handleAssign = useCallback(() => {
    if (!selectedCatalogProfileId) {
      return;
    }

    assignTargetProfile({
      companyDepartmentTargetProfile: {
        targetProfileId: selectedCatalogProfileId,
        ...(excludedCoreSkillIds.length > 0 && {
          coreCompetenciesExclusionList: excludedCoreSkillIds,
        }),
        ...(excludedHardSkillIds.length > 0 && {
          hardSkillsExclusionList: excludedHardSkillIds,
        }),
        ...(excludedToolSkillIds.length > 0 && {
          toolsExclusionList: excludedToolSkillIds,
        }),
        ...(excludedDomainKnowledgeSkillIds.length > 0 && {
          domainKnowledgeExclusionList: excludedDomainKnowledgeSkillIds,
        }),
        ...(excludedSoftSkillIds.length > 0 && {
          softSkillsExclusionList: excludedSoftSkillIds,
        }),
        ...(excludedResponsibilityIds.length > 0 && {
          responsibilitiesExclusionList: excludedResponsibilityIds,
        }),
      },
    });
  }, [
    assignTargetProfile,
    excludedCoreSkillIds,
    excludedDomainKnowledgeSkillIds,
    excludedHardSkillIds,
    excludedResponsibilityIds,
    excludedSoftSkillIds,
    excludedToolSkillIds,
    selectedCatalogProfileId,
  ]);

  const handleViewRequest = useCallback((assignmentId: string) => {
    setViewingAssignmentId(assignmentId);
  }, []);

  const handleCloseViewDialog = useCallback(() => {
    setViewingAssignmentId(null);
  }, []);

  const handleDeleteRequest = useCallback((assignmentId: string) => {
    setPendingDeleteId(assignmentId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDeleteId) {
      return;
    }

    deleteAssignment({
      method: 'delete',
      path: DeleteRoutes[Delete.DeleteDepartmentTargetProfile](companyId, departmentId, pendingDeleteId),
    });
  }, [companyId, deleteAssignment, departmentId, pendingDeleteId]);

  type TargetProfileTableRow = DepartmentTargetProfileAssignment & {
    id: string;
    onView: (assignmentId: string) => void;
    onDelete: (assignmentId: string) => void;
    viewLabel: string;
    deleteLabel: string;
  };

  const columns = useMemo(
    () => [
      createBasicTableColumn<TargetProfileTableRow>('title', t('table.title')),
      createBasicTableColumn('occupationTitle', t('table.occupation')),
      createBasicTableColumn('professionalArea', t('table.professionalArea')),
      createBasicTableColumn('actions', '', {align: 'right', maxWidth: '96px'}),
    ],
    [t]
  );

  const rowData = useMemo<TargetProfileTableRow[]>(
    () =>
      assignments.map((assignment) => ({
        ...assignment,
        id: assignment.resourceId,
        onView: handleViewRequest,
        onDelete: handleDeleteRequest,
        viewLabel: t('table.view'),
        deleteLabel: t('table.delete'),
      })),
    [assignments, handleDeleteRequest, handleViewRequest, t]
  );

  const isSectionLoading = isAssignmentsLoading || isProfessionalAreasLoading;

  if (!isWorkforceModuleActive) {
    return null;
  }

  return (
    <Box sx={{mt: 4}}>
      <Divider sx={{mb: 3}} />
      <Typography variant='h4' gutterBottom>
        {t('title')}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
        {t('subtitle')}
      </Typography>

      <Grid container spacing={2} alignItems='flex-end' sx={{mb: 3}}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size='small'
            label={t('filter.professionalArea')}
            value={professionalAreaFilter}
            onChange={(event) => setProfessionalAreaFilter(event.target.value)}
            disabled={isProfessionalAreasLoading}
            sx={getFieldStyle(theme)}
          >
            <MenuItem value={EMPTY_PROFESSIONAL_AREA} disabled>
              {t('filter.selectProfessionalArea')}
            </MenuItem>
            {(professionalAreas ?? []).map((area) => (
              <MenuItem key={area.id} value={String(area.id)}>
                {area.name[currentLanguage]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm>
          <TextField
            select
            fullWidth
            size='small'
            label={t('picker.label')}
            value={selectedCatalogProfileId}
            onChange={(event) => setSelectedCatalogProfileId(event.target.value)}
            disabled={!isProfessionalAreaSelected || isCatalogLoading}
            helperText={pickerHelperText}
            sx={getFieldStyle(theme)}
          >
            {availableCatalogProfiles.map((profile) => (
              <MenuItem key={profile.resourceId} value={profile.resourceId}>
                {profile.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm='auto'>
          <EmpionButton
            variant='primary'
            onClick={handleAssign}
            disabled={!isProfessionalAreaSelected || !selectedCatalogProfileId || isAssigning}
            sx={{width: {xs: '100%', sm: 'auto'}}}
          >
            {t('picker.add')}
          </EmpionButton>
        </Grid>
      </Grid>

      {selectedCatalogProfileId && (
        <TargetProfileDetailCard
          profile={selectedProfileDetail}
          isLoading={isDetailLoading}
          excludedCoreSkillIds={excludedCoreSkillIdSet}
          excludedHardSkillIds={excludedHardSkillIdSet}
          excludedToolSkillIds={excludedToolSkillIdSet}
          excludedDomainKnowledgeSkillIds={excludedDomainKnowledgeSkillIdSet}
          excludedSoftSkillIds={excludedSoftSkillIdSet}
          excludedResponsibilityIds={excludedResponsibilityIdSet}
          onToggleCoreExclusion={toggleCoreExclusion}
          onToggleHardExclusion={toggleHardExclusion}
          onToggleToolExclusion={toggleToolExclusion}
          onToggleDomainKnowledgeExclusion={toggleDomainKnowledgeExclusion}
          onToggleSoftExclusion={toggleSoftExclusion}
          onToggleResponsibilityExclusion={toggleResponsibilityExclusion}
        />
      )}

      {isSectionLoading ? (
        <Loading />
      ) : (
        <BasicTable
          columns={columns}
          Row={DepartmentTargetProfileRow}
          rowData={rowData}
          hideLoadMoreFooter
        />
      )}

      <DepartmentTargetProfileViewDialog
        open={!!viewingAssignmentId}
        companyId={companyId}
        departmentId={departmentId}
        assignmentId={viewingAssignmentId}
        onClose={handleCloseViewDialog}
      />

      <ConfirmDeleteDialog
        open={!!pendingDeleteId}
        dialogContent={t('deleteConfirmation')}
        handleConfirm={handleConfirmDelete}
        handleCancel={() => setPendingDeleteId(null)}
      />
    </Box>
  );
};
